import ImageIcon from "@redotech/redo-icons/image-01.svg";
import { Provider } from "@redotech/redo-model/order";
import { TrackerStatus } from "@redotech/redo-model/order-tracking/order-tracking-tracker-status-definition";
import {
  OrderTrackingOrderFulfillmentStatus,
  OrderTrackingShipmentStatus,
} from "@redotech/redo-model/order-tracking/order-tracking-types";
import {
  TRACKING_PAGE_GRID_CELL_SIZE,
  TRACKING_PAGE_GRID_GAP,
  TrackingPageBlock,
  TrackingPageSettings,
} from "@redotech/redo-model/order-tracking/tracking-page-definition";
import type { TrackingPageOrder } from "@redotech/redo-model/order-tracking/tracking-page-order-definition";
import { Flex } from "@redotech/redo-web/flex";
import { getBlockStyles } from "@redotech/redo-web/tracking-page-builder/blocks/shared/block-styles";
import { BlockContent } from "@redotech/redo-web/tracking-page-builder/builder/shared-preview";
import {
  TrackingPageContext,
  TrackingPageEnvironment,
} from "@redotech/redo-web/tracking-page-builder/context/tracking-page-context";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as css from "./tracking-page-thumbnail.module.css";

const noopAsync = async () => ({}) as any;
const noopSync = () => null;

const CELL_SIZE_WITH_GAP =
  TRACKING_PAGE_GRID_CELL_SIZE + TRACKING_PAGE_GRID_GAP;

function computeBlockBounds(blocks: TrackingPageBlock[]) {
  let minLeft = Infinity;
  let minTop = Infinity;
  let maxRight = 0;
  let maxBottom = 0;
  for (const block of blocks) {
    const left = block.anchorCoordinates.x * CELL_SIZE_WITH_GAP;
    const top = block.anchorCoordinates.y * CELL_SIZE_WITH_GAP;
    const right =
      left +
      block.size.width * TRACKING_PAGE_GRID_CELL_SIZE +
      (block.size.width - 1) * TRACKING_PAGE_GRID_GAP;
    const bottom =
      top +
      block.size.height * TRACKING_PAGE_GRID_CELL_SIZE +
      (block.size.height - 1) * TRACKING_PAGE_GRID_GAP;
    minLeft = Math.min(minLeft, left);
    minTop = Math.min(minTop, top);
    maxRight = Math.max(maxRight, right);
    maxBottom = Math.max(maxBottom, bottom);
  }
  return {
    offsetX: minLeft,
    offsetY: minTop,
    width: maxRight - minLeft,
    height: maxBottom - minTop,
  };
}

export const TrackingPageThumbnail = memo(function TrackingPageThumbnail({
  settings,
}: {
  settings: TrackingPageSettings | undefined;
}) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [wrapperSize, setWrapperSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!wrapperRef.current) {
      return;
    }
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setWrapperSize({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });
    observer.observe(wrapperRef.current);
    return () => observer.disconnect();
  }, []);

  if (!settings || settings.blocks.length === 0) {
    return (
      <div className={css.thumbnailWrapper} ref={wrapperRef}>
        <Flex align="center" height="full" justify="center" w="full">
          <ImageIcon className={css.placeholderIcon} />
        </Flex>
      </div>
    );
  }

  return (
    <div
      className={css.thumbnailWrapper}
      ref={wrapperRef}
      style={{
        backgroundColor: settings.background.backgroundColor,
        backgroundImage: settings.background.backgroundImageUrl
          ? `url(${settings.background.backgroundImageUrl})`
          : undefined,
      }}
    >
      {wrapperSize.width > 0 && (
        <ThumbnailCanvas settings={settings} wrapperSize={wrapperSize} />
      )}
    </div>
  );
});

const ThumbnailCanvas = memo(function ThumbnailCanvas({
  settings,
  wrapperSize,
}: {
  settings: TrackingPageSettings;
  wrapperSize: { width: number; height: number };
}) {
  const noop = useCallback(() => {}, []);
  const context = useMemo(() => createStubContext(settings), [settings]);

  const blockStyles = getBlockStyles(settings);

  const { canvasWidth, canvasHeight, scale, offsetX, offsetY } = useMemo(() => {
    const bounds = computeBlockBounds(settings.blocks);
    const pad = TRACKING_PAGE_GRID_CELL_SIZE * 0.5;
    const cw = bounds.width + pad * 2;
    const ch = bounds.height + pad * 2;
    const scaleX = wrapperSize.width / cw;
    const scaleY = wrapperSize.height / ch;
    return {
      canvasWidth: cw,
      canvasHeight: ch,
      scale: Math.min(scaleX, scaleY),
      offsetX: bounds.offsetX - pad,
      offsetY: bounds.offsetY - pad,
    };
  }, [settings.blocks, wrapperSize]);

  return (
    <TrackingPageContext.Provider value={context}>
      {/* Canvas dimensions, position, and scale are computed from block data at runtime */}
      <div
        className={css.thumbnailCanvas}
        style={{
          width: canvasWidth,
          height: canvasHeight,
          left: "50%",
          top: "50%",
          transform: `translate(-50%, -50%) scale(${scale})`,
          transformOrigin: "center center",
        }}
      >
        {settings.blocks.map((block, index) => {
          const width =
            block.size.width * TRACKING_PAGE_GRID_CELL_SIZE +
            (block.size.width - 1) * TRACKING_PAGE_GRID_GAP;
          const height =
            block.size.height * TRACKING_PAGE_GRID_CELL_SIZE +
            (block.size.height - 1) * TRACKING_PAGE_GRID_GAP;
          const left = block.anchorCoordinates.x * CELL_SIZE_WITH_GAP - offsetX;
          const top = block.anchorCoordinates.y * CELL_SIZE_WITH_GAP - offsetY;

          return (
            <div
              key={block._id}
              style={{
                position: "absolute",
                left,
                top,
                width,
                height,
                borderRadius: blockStyles.borderRadius,
                backgroundColor: settings.blockSettings.backgroundColor,
                overflow: "hidden",
              }}
            >
              <BlockContent block={block} index={index} onUpdate={noop} />
            </div>
          );
        })}
      </div>
    </TrackingPageContext.Provider>
  );
});

const MOCK_ORDER: TrackingPageOrder = {
  externalOrderId: "mock-order-1",
  internalMongoId: "000000000000000000000000",
  teamId: "000000000000000000000000",
  orderNumber: "1042",
  customer: {
    email: "jane@example.com",
    name: "Jane Smith",
    firstName: "Jane",
    lastName: "Smith",
  },
  fulfillmentStatus: OrderTrackingOrderFulfillmentStatus.FULFILLED,
  currency: "USD",
  source: Provider.SHOPIFY,
  shippingAddress: {
    city: "San Francisco",
    state: "CA",
    country: "United States",
    countryCode: "US",
    postalCode: "94102",
  },
  shippingMethod: "Standard Shipping",
  shipments: [
    {
      id: "mock-shipment-1",
      status: OrderTrackingShipmentStatus.IN_TRANSIT,
      trackingCode: "1Z999AA10123456784",
      carrier: "UPS",
      estimatedDeliveryAt: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000),
      updatedAt: new Date(),
      trackingEvents: [
        {
          timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000),
          status: TrackerStatus.IN_TRANSIT,
          message: "In transit to destination",
          location: { city: "Memphis", state: "TN", country: "US" },
        },
        {
          timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
          status: TrackerStatus.PRE_TRANSIT,
          message: "Shipment picked up",
          location: { city: "Los Angeles", state: "CA", country: "US" },
        },
      ],
      lineItems: [
        {
          id: "mock-item-1",
          name: "Classic T-Shirt",
          variantTitle: "Medium / Black",
          quantity: 1,
          undiscountedPrice: null,
        },
      ],
    },
  ],
  lineItems: [
    {
      id: "mock-item-1",
      name: "Classic T-Shirt",
      variantTitle: "Medium / Black",
      quantity: 1,
      undiscountedPrice: null,
    },
  ],
};

function createStubContext(settings: TrackingPageSettings) {
  return {
    stalledDays: null,
    trackingPageId: "",
    environment: TrackingPageEnvironment.BUILDER as const,
    pageSettings: settings,
    order: MOCK_ORDER,
    returnAppSettings: {
      storeUrl: "",
      merchantName: "",
      useShopifyStorefrontForCommentSold: false,
      exchanges: { shopSiteDomain: "", shopSiteURL: "" },
    },
    fulfillmentOrders: [],
    renderReturnSteps: noopSync,
    subscribeUrl: "",
    hasSubscriptionIntegration: false,
    providers: [] as Provider[],
    availableProviders: [] as Provider[],
    generateDiscountCode: noopAsync,
    productUpsell: {
      getRecommendedBestSellingProducts: async () => [],
      getRecommendedProductsByProvider: async () => ({ products: [] }),
      getAllCollections: async () => [],
      getCollection: async () => null,
      selectWindow: { current: null as any },
      getProductFilterById: async () => undefined,
    },
  };
}
